import { FunctionComponent, useRef, useState, DragEvent, MouseEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCommentSms, faEnvelope, faClock, faSplit } from '@fortawesome/pro-regular-svg-icons';
import { InsertableType } from '../useAutomationFlow';
import styles from './AddNode.module.scss';
import clsx from 'clsx';

export interface AddNodeData {
    isTerminal?: boolean;
    isBranch?: boolean;
    insertAfterStepId?: string;
    isDragging?: boolean;
    readOnly?: boolean;
    onInsert?: (type: InsertableType) => void;
    [key: string]: unknown;
}

const NODE_OPTIONS: { type: InsertableType; label: string; icon: typeof faClock; diamond?: boolean }[] = [
    { type: 'email', label: 'Send Email', icon: faEnvelope },
    { type: 'sms', label: 'Send SMS', icon: faCommentSms },
    { type: 'delay', label: 'Time Delay', icon: faClock },
    { type: 'split', label: 'Conditional Split', icon: faSplit, diamond: true }
];

const AddNode: FunctionComponent<NodeProps> = ({ data }) => {
    const { isTerminal, onInsert, isDragging, readOnly } = data as AddNodeData;
    const menuOptions = NODE_OPTIONS;
    const [isDragOver, setIsDragOver] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const dragCounter = useRef(0);
    const buttonRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;

        const close = (e: globalThis.MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target as Node)
            ) {
                setMenuOpen(false);
            }
        };

        const closeAlways = () => setMenuOpen(false);

        // capture:true so we intercept before ReactFlow stops propagation
        document.addEventListener('mousedown', close, true);
        document.addEventListener('scroll', closeAlways, true);
        document.addEventListener('wheel', closeAlways, true);

        return () => {
            document.removeEventListener('mousedown', close, true);
            document.removeEventListener('scroll', closeAlways, true);
            document.removeEventListener('wheel', closeAlways, true);
        };
    }, [menuOpen]);

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        if (e.dataTransfer.types.includes('automation/node-type')) {
            setIsDragOver(true);
        }
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) {
            setIsDragOver(false);
        }
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragOver(false);
        const type = e.dataTransfer.getData('automation/node-type') as InsertableType;
        if (type && onInsert) {
            onInsert(type);
        }
    };

    const handleButtonClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (isDragging) return;
        if (!menuOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + 8,
                left: rect.left + rect.width / 2
            });
        }
        setMenuOpen(prev => !prev);
    };

    const handleMenuItemClick = (e: MouseEvent<HTMLButtonElement>, type: InsertableType) => {
        e.stopPropagation();
        setMenuOpen(false);
        onInsert?.(type);
    };

    const menu = menuOpen
        ? createPortal(
              <div
                  ref={menuRef}
                  className={styles.menu}
                  style={{ top: menuPos.top, left: menuPos.left }}
              >
                  {menuOptions.map(opt => (
                      <button
                          key={opt.type}
                          type="button"
                          className={styles.menuItem}
                          onClick={e => handleMenuItemClick(e, opt.type)}
                      >
                          {opt.diamond ? (
                              <span className={styles.menuItemIconDiamond}>
                                  <FontAwesomeIcon
                                      icon={opt.icon}
                                      className={styles.menuItemIconInner}
                                  />
                              </span>
                          ) : (
                              <span className={`${styles.menuItemIcon} ${styles[`menuItemIcon_${opt.type}`]}`}>
                                  <FontAwesomeIcon icon={opt.icon} />
                              </span>
                          )}
                          {opt.label}
                      </button>
                  ))}
              </div>,
              document.body
          )
        : null;

    return (
        <>
            <Handle
                type="target"
                position={Position.Top}
                style={{ opacity: 0, pointerEvents: 'none' }}
            />
            <div
                className={styles.wrapper}
                onDragEnter={readOnly ? undefined : handleDragEnter}
                onDragOver={readOnly ? undefined : handleDragOver}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : handleDrop}
            >
                {!readOnly && (
                    <div
                        ref={buttonRef}
                        className={clsx(styles.addButton, {
                            [styles.dragOver]: isDragOver,
                            [styles.draggingActive]: isDragging && !isDragOver
                        })}
                        onClick={handleButtonClick}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </div>
                )}
                {isTerminal && <span className={styles.endLabel}>End</span>}
            </div>
            {menu}
            <Handle
                type="source"
                position={Position.Bottom}
                style={{ opacity: 0, pointerEvents: 'none' }}
            />
        </>
    );
};

export default AddNode;
